import React, { useMemo } from 'react';

/**
 * SVG-based area/line chart for dashboard activity panels.
 * No external dependencies — pure React + SVG.
 *
 * Props:
 *   points      — number[]  data series
 *   width       — number    SVG width (default 100%)
 *   height      — number    SVG height (default 80)
 *   color       — css color for line + area fill
 *   showDots    — bool      show data point dots
 *   showGrid    — bool      show horizontal grid lines
 *   strokeWidth — number    line stroke width
 *   animate     — bool      entrance animation
 *   labels      — string[]  optional x-axis labels (same length as points)
 */
export default function ActivityChart({
  points = [],
  height = 80,
  color = 'var(--risk-high)',
  color2 = null,
  showDots = false,
  showGrid = true,
  strokeWidth = 2,
  animate = true,
  labels = [],
  /** When both set, y-axis uses this fixed domain (required for risk score charts). */
  yMin = null,
  yMax = null,
  className = '',
}) {
  const safePoints = useMemo(
    () => points.map(Number).filter((v) => Number.isFinite(v)),
    [points]
  );

  const pathData = useMemo(() => {
    if (safePoints.length < 2) return null;
    const n = safePoints.length;
    const useFixedDomain =
      yMin != null && yMax != null && Number.isFinite(Number(yMin)) && Number.isFinite(Number(yMax));
    const minV = useFixedDomain ? Number(yMin) : Math.min(...safePoints);
    const maxV = useFixedDomain ? Number(yMax) : Math.max(...safePoints);
    const range = maxV - minV || 1;
    const pad = { top: 8, bottom: showGrid ? 24 : 8, left: 4, right: 4 };
    const w = 400; // internal viewBox width
    const h = height;
    const innerW = w - pad.left - pad.right;
    const innerH = h - pad.top - pad.bottom;

    const toX = (i) => pad.left + (i / (n - 1)) * innerW;
    const toY = (v) => pad.top + innerH - ((v - minV) / range) * innerH;

    const pts = safePoints.map((v, i) => ({ x: toX(i), y: toY(v), v }));

    // Straight segments for fixed-domain (risk) charts so dots stay on true values
    let linePath = `M ${pts[0].x} ${pts[0].y}`;
    if (useFixedDomain) {
      for (let i = 1; i < pts.length; i++) {
        linePath += ` L ${pts[i].x} ${pts[i].y}`;
      }
    } else {
      for (let i = 1; i < pts.length; i++) {
        const cp1x = pts[i - 1].x + (pts[i].x - pts[i - 1].x) * 0.45;
        const cp1y = pts[i - 1].y;
        const cp2x = pts[i].x - (pts[i].x - pts[i - 1].x) * 0.45;
        const cp2y = pts[i].y;
        linePath += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${pts[i].x} ${pts[i].y}`;
      }
    }

    const areaPath =
      linePath +
      ` L ${pts[pts.length - 1].x} ${h - pad.bottom}` +
      ` L ${pts[0].x} ${h - pad.bottom} Z`;

    const gridLines = showGrid
      ? [0.25, 0.5, 0.75].map((t) => ({
          y: pad.top + innerH * (1 - t),
          value: Math.round(minV + range * t),
        }))
      : [];

    return { linePath, areaPath, pts, gridLines, pad, w, h };
  }, [safePoints, height, showGrid, yMin, yMax]);

  if (!pathData || safePoints.length < 2) {
    return (
      <div
        className={`flex items-center justify-center text-[11px] ${className}`}
        style={{ height, color: 'var(--text-subtle)' }}
      >
        Not enough data
      </div>
    );
  }

  const { linePath, areaPath, pts, gridLines, pad, w, h } = pathData;
  const gradId = `acg-${color.replace(/[^a-z0-9]/gi, '')}`;

  return (
    <div className={`w-full overflow-hidden ${className}`}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height={h}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {gridLines.map((g, i) => (
          <line
            key={i}
            x1={pad.left}
            x2={w - pad.right}
            y1={g.y}
            y2={g.y}
            stroke="var(--border-subtle)"
            strokeWidth={1}
          />
        ))}

        {/* Area fill */}
        <path
          d={areaPath}
          fill={`url(#${gradId})`}
          style={animate ? { animation: 'fadeIn 0.6s ease' } : undefined}
        />

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={animate ? { animation: 'fadeIn 0.4s ease' } : undefined}
        />

        {/* Dots */}
        {showDots &&
          pts.map((pt, i) => (
            <circle key={i} cx={pt.x} cy={pt.y} r={3} fill={color} />
          ))}

        {/* X-axis labels */}
        {labels.length > 0 &&
          labels.map((lbl, i) => {
            if (i % Math.ceil(labels.length / 6) !== 0 && i !== labels.length - 1) return null;
            const x = pad.left + (i / (safePoints.length - 1)) * (w - pad.left - pad.right);
            return (
              <text
                key={i}
                x={x}
                y={h - 4}
                textAnchor="middle"
                fontSize={10}
                fill="var(--text-subtle)"
              >
                {lbl}
              </text>
            );
          })}
      </svg>
    </div>
  );
}
